import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { Add_Campaign_Action } from '../../Redux/Actions/Campaign_Actions';
import { useDispatch as useReduxDispatch } from 'react-redux';
import { Notify } from '../../Utility/Notify';
import AddIcon from '@mui/icons-material/Add';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

export default function AddCampaign() {
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const dispatch     = useDispatch();
  const navigate     = useNavigate();
  const sidebarWidth = useSidebarWidth();

  const [campaignname,  setCampaignname]  = useState('');
  const [cost,          setCost]          = useState('');
  const [redirectLink,  setRedirectLink]  = useState('');
  const [dateValue,     setDateValue]     = useState(null);
  const [isActive,      setIsActive]      = useState(true);
  const [loading,       setLoading]       = useState(true);
  const [sending,       setSending]       = useState(false);
  const [formErrors,    setFormErrors]    = useState({});

  let user = null;
  if (localStorage.getItem('user')) {
    user = JSON.parse(localStorage.getItem('user'));
  }

  /* ── validation ── */
  const validate = () => {
    const errors = {};
    if (!campaignname.trim()) errors.campaignname = 'Campaign name is required';
    if (!cost || parseFloat(cost) < 0) errors.cost = 'Valid cost is required';
    if (redirectLink.trim() && !/^(https?:\/\/)/.test(redirectLink))
      errors.redirectLink = 'URL must start with http:// or https://';
    if (!dateValue) errors.date = 'Date is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ── submit ── */
  const onsubmit = async () => {
    if (!validate()) return Notify('error', 'Please fix form errors', 'error');
    setLoading(true);
    setSending(true);
    await dispatch(Add_Campaign_Action({
      CampainName: campaignname.trim(),
      Date: dateValue,
      Cost: cost,
      redirectLink: redirectLink.trim(),
      isactivate: isActive,
      addby: user?.data?._id,
      updatedby: user?.data?._id,
    }));
    setLoading(false);
    setSending(false);
  };

  const resadd = useSelector((state) => state.AllCampaign.campaign);
  useEffect(() => {
    if (loading === false) {
      if (resadd?.status === 201) {
        Notify('success', 'Campaign added successfully', 'success');
        setCampaignname(''); setCost(''); setRedirectLink('');
        setDateValue(null); setIsActive(true); setFormErrors({});
        setTimeout(() => navigate('/Campaigns'), 1200);
      } else {
        Notify('error', 'Failed to add campaign', 'error');
      }
      setLoading(true);
    }
  }, [loading]);

  /* ─── shared styles ─── */
  const inputStyle = (hasError) => ({
    width: '100%', padding: '13px 16px', borderRadius: '10px',
    border: `1.5px solid ${hasError ? '#ef4444' : (isDark ? '#2d3248' : '#e2e8f0')}`,
    backgroundColor: isDark ? '#151827' : '#f8fafc',
    color: isDark ? '#f1f5f9' : '#0f172a',
    fontSize: '14px', outline: 'none', transition: 'all 0.2s',
    fontFamily: "'DM Sans',sans-serif", boxSizing: 'border-box',
  });
  const labelStyle = {
    display: 'block', fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: isDark ? '#9ca3af' : '#64748b', marginBottom: '8px',
  };
  const errorStyle = { fontSize: '12px', color: '#ef4444', margin: '5px 0 0' };
  const onFocus = (e) => {
    e.target.style.borderColor     = '#2563eb';
    e.target.style.backgroundColor = isDark ? '#1a1f35' : '#ffffff';
    e.target.style.boxShadow       = '0 0 0 3px rgba(37,99,235,0.12)';
  };
  const onBlur = (e, hasError) => {
    e.target.style.borderColor     = hasError ? '#ef4444' : (isDark ? '#2d3248' : '#e2e8f0');
    e.target.style.backgroundColor = isDark ? '#151827' : '#f8fafc';
    e.target.style.boxShadow       = 'none';
  };

  const fieldWrap = { marginBottom: '20px' };

  return (
    <div style={{
      position: 'fixed',
      top: '50px', left: sidebarWidth, right: 0, bottom: 0,
      transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
      overflowY: 'auto',
      background: isDark ? 'linear-gradient(135deg,#0f1117 0%,#1a1d2e 100%)' : '#f0f2f5',
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
      marginTop: '10px',
    }}>

      {/* ── Breadcrumb ── */}
      <div style={{
        padding: '14px 28px',
        backgroundColor: isDark ? '#1a1d2e' : '#f0f2f5',
        display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
      }}>
        <Link to="/Campaigns" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>
          Campaigns
        </Link>
        <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
        <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Add New Campaign</span>
      </div>

      {/* ── Form ── */}
      <div style={{ padding: '28px 32px 48px' }}>
        <div style={{
          width: '100%', maxWidth: '720px',
          backgroundColor: isDark ? '#1e2132' : '#ffffff',
          borderRadius: '20px', padding: '36px 40px',
          boxShadow: isDark ? '0 24px 60px rgba(0,0,0,0.5)' : '0 4px 32px rgba(0,0,0,0.08)',
          border: isDark ? '1px solid #2a2f45' : '1px solid #e8eaf0',
        }}>

          {/* title */}
          <div style={{ marginBottom: '28px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0d1b3e', margin: '0 0 4px', letterSpacing: '-0.4px' }}>
              Add New Campaign
            </h1>
            <p style={{ fontSize: '13px', color: isDark ? '#6b7280' : '#94a3b8', margin: 0 }}>
              Create a new marketing campaign in the CRM system
            </p>
          </div>

          {/* ── Campaign Name ── */}
          <div style={fieldWrap}>
            <label style={labelStyle}>Campaign Name *</label>
            <input
              style={inputStyle(!!formErrors.campaignname)}
              value={campaignname}
              onChange={(e) => { setCampaignname(e.target.value); if (formErrors.campaignname) setFormErrors(p => ({ ...p, campaignname: '' })); }}
              type="text"
              placeholder="e.g. Summer 2026 Campaign"
              onFocus={onFocus}
              onBlur={(e) => onBlur(e, !!formErrors.campaignname)}
              autoFocus
            />
            {formErrors.campaignname && <p style={errorStyle}>{formErrors.campaignname}</p>}
          </div>

          {/* ── Date ── */}
          <div style={fieldWrap}>
            <label style={labelStyle}>Campaign Date *</label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateValue ? dayjs(dateValue) : null}
                onChange={(v) => {
                  setDateValue(v ? v.format('YYYY-MM-DD') : null);
                  if (formErrors.date) setFormErrors(p => ({ ...p, date: '' }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    error: !!formErrors.date,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: isDark ? '#151827' : '#f8fafc',
                        fontSize: '14px',
                        fontFamily: "'DM Sans',sans-serif",
                        border: `1.5px solid ${formErrors.date ? '#ef4444' : (isDark ? '#2d3248' : '#e2e8f0')}`,
                        '& fieldset': { border: 'none' },
                        color: isDark ? '#f1f5f9' : '#0f172a',
                      },
                      '& .MuiInputBase-input': {
                        color: isDark ? '#f1f5f9' : '#0f172a',
                        padding: '13px 16px',
                      },
                      '& .MuiSvgIcon-root': { color: isDark ? '#9ca3af' : '#64748b' },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            {formErrors.date && <p style={errorStyle}>{formErrors.date}</p>}
          </div>

          {/* ── Cost ── */}
          <div style={fieldWrap}>
            <label style={labelStyle}>Cost ($) *</label>
            <input
              style={inputStyle(!!formErrors.cost)}
              value={cost}
              onChange={(e) => { setCost(e.target.value); if (formErrors.cost) setFormErrors(p => ({ ...p, cost: '' })); }}
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              onFocus={onFocus}
              onBlur={(e) => onBlur(e, !!formErrors.cost)}
            />
            {formErrors.cost && <p style={errorStyle}>{formErrors.cost}</p>}
          </div>

          {/* ── Redirect Link ── */}
          <div style={fieldWrap}>
            <label style={labelStyle}>Redirect Link <span style={{ fontSize: '10px', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
            <input
              style={inputStyle(!!formErrors.redirectLink)}
              value={redirectLink}
              onChange={(e) => { setRedirectLink(e.target.value); if (formErrors.redirectLink) setFormErrors(p => ({ ...p, redirectLink: '' })); }}
              type="text"
              placeholder="https://example.com/campaign"
              onFocus={onFocus}
              onBlur={(e) => onBlur(e, !!formErrors.redirectLink)}
            />
            {formErrors.redirectLink
              ? <p style={errorStyle}>{formErrors.redirectLink}</p>
              : <p style={{ fontSize: '12px', color: isDark ? '#4b5563' : '#94a3b8', margin: '5px 0 0' }}>
                  The link users will be redirected to when clicking the campaign
                </p>
            }
            {/* Link preview */}
            {redirectLink && !formErrors.redirectLink && /^(https?:\/\/)/.test(redirectLink) && (
              <div style={{
                marginTop: '8px', padding: '10px 14px', borderRadius: '8px',
                backgroundColor: isDark ? 'rgba(37,99,235,0.08)' : 'rgba(37,99,235,0.05)',
                border: `1px solid ${isDark ? 'rgba(37,99,235,0.2)' : 'rgba(37,99,235,0.15)'}`,
                fontSize: '12px', color: isDark ? '#93c5fd' : '#2563eb',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                <a href={redirectLink} target="_blank" rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {redirectLink.length > 60 ? redirectLink.slice(0, 60) + '…' : redirectLink}
                </a>
              </div>
            )}
          </div>

          {/* ── Status Toggle ── */}
          <div style={{ marginBottom: '8px' }}>
            <label style={labelStyle}>Campaign Status</label>
            <div style={{
              padding: '14px 16px', borderRadius: '10px',
              border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
              backgroundColor: isDark ? '#151827' : '#f8fafc',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* toggle pill */}
                <div
                  onClick={() => setIsActive(p => !p)}
                  style={{
                    width: '44px', height: '24px', borderRadius: '12px', cursor: 'pointer',
                    backgroundColor: isActive ? '#1d4ed8' : (isDark ? '#374151' : '#d1d5db'),
                    transition: 'background-color 0.2s', position: 'relative', flexShrink: 0,
                  }}
                >
                  <div style={{
                    position: 'absolute', top: '3px',
                    left: isActive ? '23px' : '3px',
                    width: '18px', height: '18px', borderRadius: '50%',
                    backgroundColor: '#ffffff', transition: 'left 0.2s',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }} />
                </div>
                <span style={{ fontSize: '13px', fontWeight: 600, color: isActive ? (isDark ? '#4ade80' : '#16a34a') : (isDark ? '#f87171' : '#ef4444') }}>
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <span style={{ fontSize: '12px', color: isDark ? '#6b7280' : '#94a3b8' }}>
                {isActive ? 'Campaign is live and accepting clicks' : 'Campaign is paused'}
              </span>
            </div>
          </div>

          {/* divider */}
          <div style={{ height: '1px', backgroundColor: isDark ? '#2a2f45' : '#e8eaf0', margin: '28px 0' }} />

          {/* buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={!sending ? onsubmit : null}
              disabled={sending}
              type="button"
              style={{
                flex: 1, padding: '14px 24px', borderRadius: '10px', border: 'none',
                background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)',
                color: '#fff', fontSize: '13px', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                cursor: sending ? 'not-allowed' : 'pointer',
                opacity: sending ? 0.7 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'all 0.2s', boxShadow: '0 4px 16px rgba(29,78,216,0.35)',
                fontFamily: "'DM Sans',sans-serif",
              }}
              onMouseEnter={(e) => { if (!sending) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(29,78,216,0.45)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(29,78,216,0.35)'; }}
            >
              {sending
                ? <><CircularProgress size={16} style={{ color: '#fff' }} /><span>Adding…</span></>
                : <><AddIcon sx={{ fontSize: 17 }} /><span>Add Campaign</span></>}
            </button>

            <button
              type="button"
              onClick={() => navigate('/Campaigns')}
              style={{
                padding: '14px 28px', borderRadius: '10px',
                border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
                backgroundColor: 'transparent',
                color: isDark ? '#9ca3af' : '#64748b',
                fontSize: '13px', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                cursor: 'pointer', transition: 'all 0.2s',
                fontFamily: "'DM Sans',sans-serif",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = isDark ? '#4b5563' : '#94a3b8'; e.currentTarget.style.color = isDark ? '#e5e7eb' : '#374151'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = isDark ? '#2d3248' : '#e2e8f0'; e.currentTarget.style.color = isDark ? '#9ca3af' : '#64748b'; }}
            >
              Cancel
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
